import React, { useContext, useMemo, useState } from 'react';
import { cx } from '@emotion/css';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@apollo/client';
import { FavoriteIcon, FlyersAutocomplete, DeleteSavedSearchInfo } from '@/components';
import FlyersContext from '@/context/FlyersContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerStatus } from '@/types/flyer';

import AuthenticationContext from '@/context/AuthenticationContext';
import {
  BodyLarge,
  BodyLargeEmphasized,
  BodyMedium,
  ButtonFlatSmall,
  Dialog,
} from '@/design-system';
import { sortBy } from 'lodash';
import { GET_TOP_SEARCHES } from '@/graphql/queries';
import { GetTopSearchesResponse } from '@/graphql/types';
import styles from './Search.styles';

const MAX_SEARCH_RESULTS = 5;

export const Search: React.FC = () => {
  const navigate = useNavigate();
  const { data: topSearchesData } = useQuery<GetTopSearchesResponse>(GET_TOP_SEARCHES);
  const [showAllSavedSearches, setShowAllSavedSearches] = useState<boolean>(false);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState<boolean>(false);
  const [savedSearchRemoval, setSavedSearchRemoval] = useState<string | null>();

  const { track } = useAnalytics();
  const {
    savedSearches,
    recentSearches,
    saveRecentSearches,
    addRecentSearch,
    removeRecentSearch,
    toggleSavedSearches,
  } = useContext(FlyersContext);
  const trackId = 'explore_page';

  const allSavedSearches = useMemo(
    () => sortBy(savedSearches, savedSearch => savedSearch.search.toLowerCase()),
    [savedSearches]
  );

  const flyersData = [];

  const goToResults = (search: string, origin: string) => {
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:search:${origin}`,
      label: search,
    });
    addRecentSearch(search);
    const encodedSearch = encodeURIComponent(search);
    navigate(`/explore/${encodedSearch}/results`);
  };

  const handleClickSeeAllSavedSearches = () => setShowAllSavedSearches(!showAllSavedSearches);

  const renderSavedSearches = () => {
    const results = showAllSavedSearches
      ? allSavedSearches
      : allSavedSearches.slice(0, MAX_SEARCH_RESULTS);
    return (
      <section className={styles.savedSearchesContainer}>
        <div className={styles.searchHeader}>
          <BodyLargeEmphasized data-testid="savedSearchesTitle">Saved Searches</BodyLargeEmphasized>
          {allSavedSearches.length > MAX_SEARCH_RESULTS && (
            <ButtonFlatSmall
              onClick={handleClickSeeAllSavedSearches}
              data-testid="savedSearchSeeAll"
            >
              See {showAllSavedSearches ? 'less' : 'all'}
            </ButtonFlatSmall>
          )}
        </div>
        <div>
          {results.map(({ search }) => (
            <div className={styles.savedSearches} key={search}>
              <div
                className={styles.searchItem}
                onClick={() => goToResults(search, 'saved_searches')}
                data-testid="savedSearchItem"
              >
                <FavoriteIcon size={22} />
                <BodyLarge data-testid="savedSearchName">{search}</BodyLarge>
              </div>
              <ButtonFlatSmall
                onClick={() => {
                  setSavedSearchRemoval(search);
                }}
                data-testid="savedSearchDeleteButton"
              >
                Delete
              </ButtonFlatSmall>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const onClickClearHistory = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:search:clear_history:modal`,
      label: 'Clear History',
    });
    setShowClearHistoryModal(true);
  };

  const onConfirmClearHistory = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:search:clear_history:modal:confirm`,
      label: 'Yes, clear all',
    });
    saveRecentSearches([]);
    setShowClearHistoryModal(false);
  };

  const onDismissClearHistoryModal = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:search:clear_history:modal:cancel`,
      label: 'Cancel',
    });
    setShowClearHistoryModal(false);
  };

  const onRemoveRecentSearch = (searchName: string) => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:search:remove_recent_search`,
      label: searchName,
    });
    removeRecentSearch(searchName);
  };

  const renderRecentSearches = () => {
    const results = recentSearches.slice(0, MAX_SEARCH_RESULTS);
    return (
      <>
        {savedSearches.length > 0 && <div className={styles.separator} />}
        <section className={styles.recentSearchesContainer}>
          <div className={styles.searchHeader}>
            <BodyLargeEmphasized data-testid="recentSearchesTitle">
              Recent Searches
            </BodyLargeEmphasized>
            <ButtonFlatSmall
              onClick={onClickClearHistory}
              data-testid="recentSearchesClearHistory"
              className={styles.linkButton}
            >
              Clear History
            </ButtonFlatSmall>
          </div>
          <div>
            {results.map(item => (
              <div className={styles.searchRow} key={item}>
                <div
                  className={styles.searchItem}
                  onClick={() => goToResults(item, 'recent_searches')}
                  data-testid="recentSearchItem"
                >
                  <AccessTimeIcon sx={{ fontSize: 20 }} className={styles.iconStone} />
                  <BodyLarge data-testid="recentSearchName">{item}</BodyLarge>
                </div>
                <div
                  onClick={() => onRemoveRecentSearch(item)}
                  data-testid="recentSearchRemoveButton"
                >
                  <CloseIcon sx={{ fontSize: 20 }} className={styles.closeIcon} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </>
    );
  };

  const renderTopSearches = () => (
    <>
      {(savedSearches.length > 0 || recentSearches.length > 0) && (
        <div className={styles.separator} />
      )}
      <section className={cx(styles.topSearchesContainer)}>
        <BodyLargeEmphasized data-testid="topSearchesTitle">Top Searches</BodyLargeEmphasized>

        {topSearchesData?.topSearches.map(item => (
          <div className={styles.searchRow}>
            <div
              className={styles.searchItem}
              key={item}
              onClick={() => goToResults(item, 'top_searches')}
              data-testid="topSearchItem"
            >
              <SearchIcon sx={{ fontSize: 22 }} className={styles.iconStone} />
              <BodyLarge data-testid="topSearchName">{item}</BodyLarge>
            </div>
          </div>
        ))}
      </section>
    </>
  );

  const handleDeleteSavedSearch = () => {
    if (savedSearchRemoval) {
      toggleSavedSearches(savedSearchRemoval);
      setSavedSearchRemoval(null);
    }
  };

  return (
    <>
      <Dialog
        isOpen={showClearHistoryModal}
        title="Clear history"
        description={
          <BodyMedium>Are you sure you want to clear all of your Recent Searches?</BodyMedium>
        }
        confirmButtonText="Yes, clear all"
        onCancel={onDismissClearHistoryModal}
        onConfirm={onConfirmClearHistory}
      />

      <Dialog
        isOpen={!!savedSearchRemoval}
        title={DeleteSavedSearchInfo.title}
        description={DeleteSavedSearchInfo.description}
        confirmButtonText={DeleteSavedSearchInfo.confirmButtonText}
        onCancel={() => setSavedSearchRemoval(null)}
        onConfirm={handleDeleteSavedSearch}
      />
      <FlyersAutocomplete
        trackId={trackId}
        flyers={flyersData}
        goToResults={goToResults}
        hideBackButton
      >
        <>
          {savedSearches.length > 0 && renderSavedSearches()}
          {recentSearches.length > 0 && renderRecentSearches()}
          {topSearchesData && topSearchesData.topSearches.length > 0 && renderTopSearches()}
        </>
      </FlyersAutocomplete>
    </>
  );
};

export default Search;
